import { Box, Typography } from '@mui/material';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import type ReportParagraphProps from './ReportsParagraph.types.ts';

const ReportParagraph = ({
  children,
  fontSize = '14px'
}: ReportParagraphProps) => {
  if (!children) return null;

  // Process each line separately to maintain paragraph structure
  const lines = children.split('\n');

  return (
    <Box sx={{ position: 'relative', width: '100%', zIndex: 2 }}>
      <Typography variant="body1" sx={{ fontSize, lineHeight: 1.5 }}>
        {lines.map((line, lineIndex) => {
          // Skip processing for empty lines, just render the break
          if (!line.trim()) {
            return <br key={`br-${uuidv4()}`} />;
          }

          // Process bold tags
          const words = [];
          let lastIndex = 0;
          let boldMatch: RegExpExecArray | null;
          const boldRegex = /<b>(.*?)<\/b>/g;

          while ((boldMatch = boldRegex.exec(line)) !== null) {
            // Add text before the bold tag
            if (boldMatch.index > lastIndex) {
              words.push({
                type: 'text',
                content: line.substring(lastIndex, boldMatch.index),
                key: `text-${lineIndex}-${lastIndex}`
              });
            }

            // Add the bold content
            words.push({
              type: 'bold',
              content: boldMatch[1], // The content inside <b> tags
              key: `bold-${lineIndex}-${boldMatch.index}`
            });

            lastIndex = boldMatch.index + boldMatch[0].length;
          }

          // Add any remaining text after the last bold tag
          if (lastIndex < line.length) {
            words.push({
              type: 'text',
              content: line.substring(lastIndex),
              key: `text-${lineIndex}-${lastIndex}`
            });
          }

          // If no bold tags were found, just add the whole line
          if (words.length === 0) {
            words.push({
              type: 'text',
              content: line,
              key: `text-${lineIndex}-0`
            });
          }

          return (
            <React.Fragment key={`line-${uuidv4()}`}>
              {words.map((word) =>
                word.type === 'bold' ? (
                  <strong key={word.key}>{word.content}</strong>
                ) : (
                  <React.Fragment key={word.key}>{word.content}</React.Fragment>
                )
              )}
              {lineIndex < lines.length - 1 && <br />}
            </React.Fragment>
          );
        })}
      </Typography>
    </Box>
  );
};

export default ReportParagraph;
